/**
 * Card Slider Component
 * Lightweight slider with CSS scroll-snap and JS enhancement for navigation
 */
(function() {
  'use strict';

  class CardSlider {
    constructor(container) {
      this.container = container;
      this.track = container.querySelector('[data-slider-track]');
      this.prevBtn = container.querySelector('[data-slider-prev]');
      this.nextBtn = container.querySelector('[data-slider-next]');
      this.pagination = container.querySelector('[data-slider-pagination]');
      this.dots = this.pagination ? Array.from(this.pagination.querySelectorAll('.card-slider__dot')) : [];
      this.slides = Array.from(this.track.children);

      if (this.slides.length === 0) return;

      this.currentIndex = 0;
      this.isDragging = false;
      this.startX = 0;
      this.scrollLeft = 0;

      this.init();
    }

    init() {
      this.bindEvents();
      this.updateButtonStates();
      this.observeSlides();
    }

    bindEvents() {
      // Navigation buttons
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.navigate(-1));
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.navigate(1));
      }

      // Pagination dots
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });

      // Update button states on scroll
      this.track.addEventListener('scroll', () => {
        this.updateButtonStates();
      }, { passive: true });

      // Mouse drag support
      this.track.addEventListener('mousedown', (e) => this.startDrag(e));
      this.track.addEventListener('mousemove', (e) => this.drag(e));
      this.track.addEventListener('mouseup', () => this.endDrag());
      this.track.addEventListener('mouseleave', () => this.endDrag());

      // Prevent link clicks during drag
      this.track.addEventListener('click', (e) => {
        if (this.wasDragging) {
          e.preventDefault();
          this.wasDragging = false;
        }
      });
    }

    startDrag(e) {
      this.isDragging = true;
      this.startX = e.pageX - this.track.offsetLeft;
      this.scrollLeft = this.track.scrollLeft;
      this.track.style.scrollSnapType = 'none';
      this.track.style.scrollBehavior = 'auto';
    }

    drag(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      const x = e.pageX - this.track.offsetLeft;
      const walk = (x - this.startX) * 1.5; // Scroll speed multiplier
      this.track.scrollLeft = this.scrollLeft - walk;

      // Track if we actually dragged
      if (Math.abs(walk) > 5) {
        this.wasDragging = true;
      }
    }

    endDrag() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.style.scrollSnapType = 'x mandatory';
      this.track.style.scrollBehavior = 'smooth';
    }

    navigate(direction) {
      const slideWidth = this.slides[0].offsetWidth;
      const gap = parseInt(getComputedStyle(this.track).gap) || 16;
      const scrollAmount = (slideWidth + gap) * direction;

      this.track.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }

    goToSlide(index) {
      const slide = this.slides[index];
      if (!slide) return;

      slide.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }

    updateButtonStates() {
      if (!this.prevBtn || !this.nextBtn) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      this.prevBtn.disabled = atStart;
      this.nextBtn.disabled = atEnd;
    }

    observeSlides() {
      if (this.dots.length === 0) return;

      const observerOptions = {
        root: this.track,
        threshold: 0.5
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = this.slides.indexOf(entry.target);
            if (index !== -1) {
              this.updatePagination(index);
            }
          }
        });
      }, observerOptions);

      this.slides.forEach(slide => observer.observe(slide));
    }

    updatePagination(activeIndex) {
      this.dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
      this.currentIndex = activeIndex;
    }
  }

  // Initialize all sliders on the page
  function initSliders() {
    const sliders = document.querySelectorAll('[data-card-slider]');
    sliders.forEach(slider => new CardSlider(slider));
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSliders);
  } else {
    initSliders();
  }

  // Re-initialize when Shopify section is loaded/updated (for theme editor)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initSliders);
  }
})();
